import React, { useState, useEffect } from "react";
import { Alert, Collapse, Button, Loader, Modal } from "@mantine/core";
import { IconCash, IconInfoCircle, IconChevronDown, IconChevronUp, IconFileText, IconDownload } from "@tabler/icons-react";
import fetchApi from "../components/Lib/api";
import Mixpanel from "../components/Lib/mixpanel";
import useUserStore from "../stores/userStore";

const statusBadgeClass = (status) => {
  switch (status) {
    case "accepted":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    case "reviewed":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-yellow-100 text-yellow-800";
  }
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
};

const ProjectAlert = ({ startupSlug, owner = false, member = false, openForApplication = true, currentUserId: currentUserIdProp = null }) => {
  const isOwner = owner;
  const isMember = member;
  const { user } = useUserStore((state) => ({ user: state.user }));

  const [isOpen, setIsOpen] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [openForApplications, setOpenForApplications] = useState(openForApplication);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [desiredNextOpen, setDesiredNextOpen] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [fetchedApplications, setFetchedApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationsError, setApplicationsError] = useState(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState(null);
  const [projectDetails, setProjectDetails] = useState(null); // { description, details, files[] }

  useEffect(() => {
    setOpenForApplications(openForApplication);
  }, [openForApplication]);

  useEffect(() => {
    const fetchApplications = async () => {
      if (!startupSlug) {
        setFetchedApplications([]);
        setApplicationsError(null);
        return;
      }

      setApplicationsLoading(true);
      setApplicationsError(null);

      const startTime = performance.now();

      try {
        const response = await fetchApi(`/startup_entities/${startupSlug}/applications`, "GET");
        const endTime = performance.now();
        const responseTime = Math.round(endTime - startTime);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to fetch applications.");
        }

        const data = await response.json();
        const applications = Array.isArray(data.applications) ? data.applications : [];
        const applicationsWithHref = applications.map((app) => ({
            ...app,
            href: `/applications/${app.slug}`,
          }));

          setFetchedApplications(applicationsWithHref);
        if (typeof data.open_for_applications === "boolean") {
          setOpenForApplications(data.open_for_applications);
        }

        Mixpanel.performance("Fetch Project Applications", {
            response_time_ms: responseTime,
          status: "success",
            applications_count: applicationsWithHref.length,
            project_slug: startupSlug,
          user_role: isOwner ? "owner" : isMember ? "member" : "guest",
        });
      } catch (err) {
        const endTime = performance.now();
        const responseTime = Math.round(endTime - startTime);
        console.error("Error fetching applications:", err);
        setApplicationsError(err.message || "Could not load applications.");

        Mixpanel.error("Fetch Project Applications Error", {
          error_message: err.message,
          error_type: "API Error",
          component: "ProjectAlert",
          user_action: "Load Project Applications",
          startup_slug: startupSlug,
          response_time_ms: responseTime,
          user_role: isOwner ? "owner" : isMember ? "member" : "guest",
        });
      } finally {
        setApplicationsLoading(false);
      }
    };

    fetchApplications();
  }, [startupSlug, isOpen, isOwner, isMember]);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!startupSlug || !isOpen) return;
      setProjectLoading(true);
      setProjectError(null);
      try {
        const res = await fetchApi(`/startup_entities/${startupSlug}`, "GET");
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || "Failed to load project details.");
        }
        const data = await res.json();
        // Expecting { startup_entity: { description, details, files: [{id, filename, content_type, byte_size, url}] } }
        setProjectDetails(data.startup_entity || null);
      } catch (e) {
        setProjectError(e.message || "Failed to load project details.");
      } finally {
        setProjectLoading(false);
      }
    };
    fetchProjectDetails();
  }, [startupSlug, isOpen]);

  const toggleCollapse = () => {
    setIsOpen((prev) => !prev);
  };

  const requestToggleApplications = () => {
    if (!isOwner || !startupSlug) return;
    const nextState = !openForApplications;
    setDesiredNextOpen(nextState);
    setConfirmOpen(true);
  };

  const performToggleApplications = async () => {
    if (desiredNextOpen === null) return;

    setActionLoading(true);
    setError(null);
    setStatusMessage(null);

    try {
      const response = await fetchApi(`/startup_entities/${startupSlug}`, "PUT", {
        startup_entity: { open_fo_applications: desiredNextOpen },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Unable to update application status.");
      }

      setOpenForApplications(desiredNextOpen);
      setStatusMessage(
        desiredNextOpen
          ? "Applications reopened. Applicants can submit new proposals."
          : "Applications closed. No new proposals can be submitted."
      );

      Mixpanel.settings("Project Applications", desiredNextOpen ? "Reopened" : "Closed", {
        startup_slug: startupSlug,
        new_state: desiredNextOpen ? "open" : "closed",
      });
    } catch (err) {
      console.error("Error toggling applications:", err);
      setError(err.message || "Unable to update application status.");
    } finally {
      setActionLoading(false);
      setConfirmOpen(false);
      setDesiredNextOpen(null);
    }
  };

  const requestDeleteProject = () => {
    if (!isOwner || !startupSlug) return;
    setDeleteConfirmOpen(true);
  };

  const performDeleteProject = async () => {
    if (!isOwner || !startupSlug) return;
    setDeleteLoading(true);
    setError(null);

    try {
      const response = await fetchApi(`/startup_entities/${startupSlug}`, "DELETE");
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to delete project.");
      }
      Mixpanel.settings("Project", "Deleted", { project_slug: startupSlug });
      window.location.href = "/dashboard/#/";
    } catch (err) {
      console.error("Error deleting project:", err);
      setError(err.message || "Failed to delete project.");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  const effectiveUserId = currentUserIdProp || user?.id;
  const effectiveUserIdStr = effectiveUserId != null ? String(effectiveUserId) : null;

  const handleConfirmApply = async () => {
    setError(null);

    if (!openForApplications) {
      setError("Applications are currently closed.");
      return;
    }

    if (!startupSlug) {
      setError("Startup slug is missing. Cannot apply.");
      return;
    }

    // Include solo and squad applications where user is owner or member (id-safe)
    const userApplications = fetchedApplications.filter((app) => {
      if (!effectiveUserIdStr) return false;
      if (Array.isArray(app.applicant_user_ids)) {
        return app.applicant_user_ids.map((v) => String(v)).includes(effectiveUserIdStr);
      }
      // Fallback compatibility
      const currentUserIdNum = Number(effectiveUserId);
      const currentUserIdStr = effectiveUserIdStr;
      if (app.user && !app.squad) {
        const appUserIdNum = Number(app.user.id);
        const appUserIdStr = String(app.user.id);
        if (appUserIdNum === currentUserIdNum || appUserIdStr === currentUserIdStr) return true;
      }
      if (app.squad && (app.squad.owner_id !== undefined && app.squad.owner_id !== null)) {
        const ownerIdNum = Number(app.squad.owner_id);
        const ownerIdStr = String(app.squad.owner_id);
        if (ownerIdNum === currentUserIdNum || ownerIdStr === currentUserIdStr) return true;
      }
      if (app.squad && Array.isArray(app.squad.members)) {
        if (app.squad.members.some((m) => Number(m.id) === currentUserIdNum || String(m.id) === currentUserIdStr)) {
          return true;
        }
      }
      return false;
    });
    const activeUserApplication = userApplications.find((app) => app.status !== "rejected");

    if (activeUserApplication) {
      window.location.href = `/applications/${activeUserApplication.slug}`;
      return;
    }

    const applicationPayload = {
      application: {
        pitch: "This is a default application pitch.",
      },
    };

    setApplyLoading(true);

    const startTime = performance.now();

    try {
      const response = await fetchApi(
        `/startup_entities/${startupSlug}/applications`,
        "POST",
        applicationPayload
      );

      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || errorData.message || "Failed to create application"
        );
      }

      const data = await response.json();

      if (data.application && data.application.slug) {
        Mixpanel.custom("Project Application Created", {
          startup_slug: startupSlug,
          application_id: data.application.slug,
          response_time_ms: responseTime,
          status: "success",
      });
        window.location.href = `/applications/${data.application.slug}`;
      } else {
        throw new Error("Application slug not returned from API.");
      }
    } catch (err) {
      console.error("Error creating application:", err);
      setError(err.message || "An unexpected error occurred.");
      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);
      Mixpanel.error("Create Application Error", {
        error_message: err.message,
        error_type: "API Error",
        component: "ProjectAlert",
        user_action: "Click Apply Now",
        startup_slug: startupSlug,
        response_time_ms: responseTime,
      });
    } finally {
      setApplyLoading(false);
    }
  };

  // Include solo and squad applications where user is owner or member (id-safe comparison)
  const userApplications = fetchedApplications.filter((app) => {
    if (!effectiveUserIdStr) return false;
    if (Array.isArray(app.applicant_user_ids)) {
      return app.applicant_user_ids.map((v) => String(v)).includes(effectiveUserIdStr);
    }
    // Fallback compatibility
    const currentUserIdNum = Number(effectiveUserId);
    const currentUserIdStr = effectiveUserIdStr;
    if (app.user && !app.squad) {
      const appUserIdNum = Number(app.user.id);
      const appUserIdStr = String(app.user.id);
      if (appUserIdNum === currentUserIdNum || appUserIdStr === currentUserIdStr) return true;
    }
    if (app.squad && (app.squad.owner_id !== undefined && app.squad.owner_id !== null)) {
      const ownerIdNum = Number(app.squad.owner_id);
      const ownerIdStr = String(app.squad.owner_id);
      if (ownerIdNum === currentUserIdNum || ownerIdStr === currentUserIdStr) return true;
    }
    if (app.squad && Array.isArray(app.squad.members)) {
      if (app.squad.members.some((m) => Number(m.id) === currentUserIdNum || String(m.id) === currentUserIdStr)) {
        return true;
      }
    }
    return false;
  });
  
  // Find the most relevant application to display:
  // 1. Prefer non-rejected applications (active ones)
  // 2. Fall back to rejected applications if that's all there is
  const activeUserApplication = userApplications.find((app) => app.status !== "rejected") 
    || userApplications.find((app) => app.status === "rejected")
    || userApplications[0]; // Fall back to any application if status is unexpected

  const acceptedApplications = fetchedApplications.filter((app) => app.status === "accepted");
  const incomingApplications = fetchedApplications.filter((app) => app.status !== "accepted");

  const acceptedCount = acceptedApplications.length;
  const submittedCount = fetchedApplications.filter((app) => app.status === "submitted").length;
  const pendingCount = fetchedApplications.filter((app) => app.status === "pending").length;

  const statusChip = openForApplications
    ? "bg-green-100 text-green-700"
    : "bg-gray-200 text-gray-700";

  // For applicants: show their application status if they have one (including rejected), otherwise show apply option
  const hasUserApplication = userApplications.length > 0;

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3">
          {isOwner ? (
        // Owner view
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <IconInfoCircle size={18} className="text-gray-500" />
            <div>
              <h3 className="text-base font-semibold text-gray-900">Applications</h3>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                <span className={statusChip + " px-2 py-0.5 rounded-full text-xs"}>
                  {openForApplications ? "Open" : "Closed"}
                </span>
                <span>•</span>
                <span>Accepted: {acceptedCount}</span>
                <span>•</span>
                <span>Submitted: {submittedCount}</span>
                <span>•</span>
                <span>Pending: {pendingCount}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
              <Button
              size="xs"
              variant={openForApplications ? "outline" : "filled"}
              color={openForApplications ? "red" : "green"}
              onClick={requestToggleApplications}
              loading={actionLoading}
            >
              {openForApplications ? "Close" : "Reopen"}
            </Button>
            {!openForApplications && (
              <Button size="xs" color="red" variant="light" onClick={requestDeleteProject}>
                Delete Project
              </Button>
            )}
            <Button size="xs" variant="light" onClick={toggleCollapse} rightSection={isOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}>
              {isOpen ? "Hide" : "View"}
            </Button>
          </div>
        </div>
      ) : (
        // Applicant view - two states
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <IconCash size={18} className="text-gray-500" />
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                {hasUserApplication 
                  ? (activeUserApplication?.squad 
                      ? `Your Squad Application (${activeUserApplication.squad.name})` 
                      : "Your Application")
                  : "Apply to Project"}
              </h3>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                <span className={statusChip + " px-2 py-0.5 rounded-full text-xs"}>
                  {openForApplications ? "Open" : "Closed"}
                </span>
                {hasUserApplication && activeUserApplication && (
                  <>
                    <span>•</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${statusBadgeClass(activeUserApplication.status)}`}>
                      {activeUserApplication.status || "pending"}
                    </span>
                    {!openForApplications && (
                      <>
                        <span>•</span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">
                          Applications closed
                        </span>
                      </>
                    )}
            </>
          )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasUserApplication ? (
              <>
                <Button size="xs" variant="light" onClick={toggleCollapse} rightSection={isOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}>
                  {isOpen ? "Hide" : "View"}
                </Button>
                <Button size="xs" onClick={() => window.location.href = `/applications/${activeUserApplication.slug}`}>
                  Open Application
                </Button>
              </>
            ) : (
              <>
                <Button size="xs" variant="light" onClick={toggleCollapse} rightSection={isOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}>
                  {isOpen ? "Hide" : "View Details"}
                </Button>
                <Button size="xs" onClick={handleConfirmApply} loading={applyLoading} disabled={applyLoading || !openForApplications}>
                  Apply Now
                </Button>
              </>
            )}
          </div>
        </div>
      )}

        {error && (
        <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

      {statusMessage && (
        <Alert color="teal" variant="light">
          {statusMessage}
          </Alert>
        )}

        <Collapse in={isOpen}>
        <div className="space-y-6 mt-4 pt-4 border-t border-gray-200">
            {applicationsLoading ? (
            <div className="flex items-center gap-3 text-gray-600 py-4">
                <Loader size="sm" />
              <span>Loading applications…</span>
            </div>
            ) : applicationsError ? (
            <Alert color="red" variant="light">
                {applicationsError}
              </Alert>
          ) : isOwner ? (
            // Owner view: show all applications
            <div className="space-y-6">
              {fetchedApplications.length === 0 ? (
                <p className="text-gray-500 py-4">No applications received yet.</p>
              ) : (
                <>
                  {acceptedApplications.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Accepted Teams</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {acceptedApplications.map((app) => {
                        const submittedAt = app.submitted_at || app.applied_at;
                        const acceptedProject = app.accepted_startup_entity;
                        const isSquadApplication = app.squad && app.squad.id;
                        const applicantName = isSquadApplication 
                          ? app.squad.name 
                          : (app.user ? `${app.user.first_name} ${app.user.last_name}` : "Unknown");
                        const applicantInitial = (applicantName && applicantName[0]) ? applicantName[0].toUpperCase() : "?";
                        const applicantLabel = isSquadApplication ? "Squad" : "Individual";
                        return (
                          <div
                            key={app.id}
                            className="p-4 rounded-lg border border-emerald-200 bg-emerald-50 shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-6 h-6 rounded-full bg-emerald-200 flex items-center justify-center text-xs font-semibold text-emerald-900 flex-shrink-0">
                                  {applicantInitial}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-base font-semibold text-emerald-900 truncate">
                                    {applicantName}
                                  </p>
                                  {isSquadApplication && app.squad.members && (
                                    <p className="text-xs truncate">
                                      {app.squad.members.length} member{app.squad.members.length !== 1 ? 's' : ''}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-200 text-green-900 whitespace-nowrap">Accepted</span>
                            </div>
                            {formatDate(submittedAt) && (
                              <p className="text-xs mb-2">
                                Applied {formatDate(submittedAt)}
                              </p>
                            )}
                            <p className="text-sm text-emerald-900 line-clamp-2 mb-3">
                              {app.pitch || "No introduction provided."}
                            </p>
                            <div className="flex items-center gap-3">
                              {acceptedProject && (
                                <Button
                                  size="xs"
                                  variant="filled"
                                  color="green"
                                  component="a"
                                  href={`/projects/${acceptedProject.slug}`}
                                  aria-label={`Open workspace for ${acceptedProject.name}`}
                                >
                                  Workspace
                                </Button>
                              )}
                              <Button
                                size="xs"
                                variant="light"
                                component="a"
                                href={app.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`View full application from ${isSquadApplication ? app.squad.name : (app.user ? `${app.user.first_name} ${app.user.last_name}` : 'Unknown')}`}
                              >
                                Details
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-4">Incoming Proposals</h4>
                  {incomingApplications.length === 0 ? (
                    <p className="text-gray-500 py-4">No pending proposals at the moment.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {incomingApplications.map((app) => {
                        const submittedAt = app.submitted_at || app.applied_at;
                        const isSquadApplication = app.squad && app.squad.id;
                        const applicantName = isSquadApplication 
                          ? app.squad.name 
                          : (app.user ? `${app.user.first_name} ${app.user.last_name}` : "Unknown");
                        const applicantInitial = (applicantName && applicantName[0]) ? applicantName[0].toUpperCase() : "?";
                        return (
                <a
                  key={app.id}
                  href={app.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block no-underline"
                >
                            <div className="p-4 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm hover:shadow-md">
                              <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-xs font-semibold text-blue-900 flex-shrink-0">
                                    {applicantInitial}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-base font-medium text-blue-700 truncate">
                                      {applicantName}
                                    </p>
                                    {isSquadApplication && app.squad.members && (
                                      <p className="text-xs text-blue-600 truncate">
                                        {app.squad.members.length} member{app.squad.members.length !== 1 ? 's' : ''}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusBadgeClass(app.status)}`}>
                                  {app.status || "pending"}
                                </span>
                              </div>
                              {formatDate(submittedAt) && (
                                <p className="text-xs text-gray-500 mb-2">
                                  Applied {formatDate(submittedAt)}
                        </p>
                      )}
                              <p className="text-sm text-gray-700 line-clamp-3 mb-2">
                                {app.pitch || "No introduction provided."}
                              </p>
                              <p className="text-xs text-gray-500 mt-3">Click to view full application</p>
                    </div>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Project details and files - visible to all when expanded */}
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Project details</h4>
                  {projectLoading ? (
                    <div className="flex items-center gap-2 text-gray-600"><Loader size="sm" /> <span>Loading…</span></div>
                  ) : projectError ? (
                    <Alert color="red" variant="light">{projectError}</Alert>
                  ) : projectDetails ? (
                    <div className="space-y-3">
                      {projectDetails.details ? (
                        <p className="text-sm text-gray-700 whitespace-pre-line">{projectDetails.details}</p>
                      ) : (
                        projectDetails.description && (
                          <p className="text-sm text-gray-700 whitespace-pre-line">{projectDetails.description}</p>
                        )
                      )}
                      {Array.isArray(projectDetails.files) && projectDetails.files.length > 0 && (
                        <div className="space-y-2 mt-3">
                          <h5 className="text-xs font-semibold text-gray-600">Attached files</h5>
                          <div className="space-y-2">
                            {projectDetails.files.map((f) => (
                              <div key={f.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center gap-3 min-w-0">
                                  <IconFileText size={18} className="text-green-600 flex-shrink-0" />
                                  <a className="text-sm font-medium text-gray-800 hover:underline truncate" href={f.url} target="_blank" rel="noopener noreferrer">
                                    {f.filename}
                                  </a>
                                  {typeof f.byte_size === "number" && (
                                    <span className="text-xs text-gray-500 shrink-0">({(f.byte_size / 1024 / 1024).toFixed(1)} MB)</span>
                                  )}
                                </div>
                                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900" title="Download">
                                  <IconDownload size={16} />
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No project details available.</p>
                  )}
                </div>
                </>
              )}
            </div>
          ) : hasUserApplication ? (
            // Applicant view: show their application
            activeUserApplication ? (
              <div className={`p-4 rounded-lg border ${
                activeUserApplication.status === "rejected" 
                  ? "border-red-200 bg-red-50" 
                  : activeUserApplication.status === "accepted"
                  ? "border-green-200 bg-green-50"
                  : "border-blue-200 bg-blue-50"
              }`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className={`text-base font-semibold mb-1 ${
                      activeUserApplication.status === "rejected"
                        ? "text-red-900"
                        : activeUserApplication.status === "accepted"
                        ? "text-green-900"
                        : "text-blue-900"
                    }`}>Your Application</p>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusBadgeClass(activeUserApplication.status)}`}>
                      {activeUserApplication.status || "pending"}
                    </span>
                  </div>
                </div>
                {formatDate(activeUserApplication.submitted_at || activeUserApplication.applied_at) && (
                  <p className={`text-xs mb-3 ${
                    activeUserApplication.status === "rejected"
                      ? "text-red-700"
                      : activeUserApplication.status === "accepted"
                      ? "text-green-700"
                      : "text-blue-700"
                  }`}>
                    Applied {formatDate(activeUserApplication.submitted_at || activeUserApplication.applied_at)}
                  </p>
                )}
                <p className={`text-sm mb-4 line-clamp-3 ${
                  activeUserApplication.status === "rejected"
                    ? "text-red-900"
                    : activeUserApplication.status === "accepted"
                    ? "text-green-900"
                    : "text-blue-900"
                }`}>
                  {activeUserApplication.pitch || "No introduction provided."}
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  {activeUserApplication.status === "accepted" && activeUserApplication.accepted_startup_entity && (
                    <Button
                      size="sm"
                      onClick={() => window.location.href = `/projects/${activeUserApplication.accepted_startup_entity.slug}`}
                      className="w-full sm:w-auto"
                      variant="filled"
                      color="green"
                    >
                      Open Workspace
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => window.location.href = `/applications/${activeUserApplication.slug}`}
                    className="w-full sm:w-auto"
                    variant={activeUserApplication.status === "accepted" ? "outline" : "filled"}
                  >
                    View Full Application
                  </Button>
                </div>
            </div>
            ) : (
              <p className="text-gray-500 py-4">Loading your application…</p>
            )
          ) : (
            // Applicant view: show project info and apply option
            <div className="space-y-4">
              <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Project Information</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Review the project details and requirements before applying. Make sure your proposal aligns with the project's goals.
                </p>
                {!openForApplications && (
                  <Alert color="yellow" variant="light" className="mb-4">
                    Applications are currently closed for this project.
                  </Alert>
                )}
                <Button
                  size="sm"
                  onClick={handleConfirmApply}
                  loading={applyLoading}
                  disabled={applyLoading || !openForApplications}
                  className="w-full md:w-auto"
                >
                  {openForApplications ? "Apply to This Project" : "Applications Closed"}
                </Button>
              </div>
              {/* Project details and files - visible to all when expanded */}
              <div className="p-4 rounded-lg border border-gray-200 bg-white">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Project details</h4>
                {projectLoading ? (
                  <div className="flex items-center gap-2 text-gray-600"><Loader size="sm" /> <span>Loading…</span></div>
                ) : projectError ? (
                  <Alert color="red" variant="light">{projectError}</Alert>
                ) : projectDetails ? (
                  <div className="space-y-3">
                    {projectDetails.details ? (
                      <p className="text-sm text-gray-700 whitespace-pre-line">{projectDetails.details}</p>
                    ) : (
                      projectDetails.description && (
                        <p className="text-sm text-gray-700 whitespace-pre-line">{projectDetails.description}</p>
                      )
                    )}
                    {Array.isArray(projectDetails.files) && projectDetails.files.length > 0 && (
                      <div className="space-y-2 mt-3">
                        <h5 className="text-xs font-semibold text-gray-600">Attached files</h5>
                        <div className="space-y-2">
                          {projectDetails.files.map((f) => (
                            <div key={f.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                              <div className="flex items-center gap-3 min-w-0">
                                <IconFileText size={18} className="text-green-600 flex-shrink-0" />
                                <a className="text-sm font-medium text-gray-800 hover:underline truncate" href={f.url} target="_blank" rel="noopener noreferrer">
                                  {f.filename}
                                </a>
                                {typeof f.byte_size === "number" && (
                                  <span className="text-xs text-gray-500 shrink-0">({(f.byte_size / 1024 / 1024).toFixed(1)} MB)</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900" title="Download">
                                  <IconDownload size={16} />
                                </a>
                                {isOwner && (
                                  <button
                                    className="text-red-600 hover:text-red-800"
                                    title="Delete file"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      if (!confirm('Delete this file?')) return;
                                      try {
                                        const res = await fetchApi(`/startup_entities/${startupSlug}/files/${f.id}`, 'DELETE');
                                        if (!res.ok) {
                                          const data = await res.json().catch(() => ({}));
                                          throw new Error(data.message || 'Failed to delete file');
                                        }
                                        setProjectDetails((prev) => {
                                          if (!prev) return prev;
                                          return {
                                            ...prev,
                                            files: (prev.files || []).filter((pf) => pf.id !== f.id),
                                          };
                                        });
                                      } catch (err) {
                                        console.error(err);
                                        setProjectError(err.message || 'Failed to delete file');
                                      }
                                    }}
                                  >
                                    {/* simple X icon using SVG to avoid importing more icons */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No project details available.</p>
                )}
              </div>
            </div>
            )}
          </div>
        </Collapse>
        {/* Ensure project details are visible even if there are no applications (owner view) */}
        {isOwner && (!fetchedApplications || fetchedApplications.length === 0) && isOpen && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Project details</h4>
            {projectLoading ? (
              <div className="flex items-center gap-2 text-gray-600"><Loader size="sm" /> <span>Loading…</span></div>
            ) : projectError ? (
              <Alert color="red" variant="light">{projectError}</Alert>
            ) : projectDetails ? (
              <div className="space-y-3">
                {projectDetails.details ? (
                  <p className="text-sm text-gray-700 whitespace-pre-line">{projectDetails.details}</p>
                ) : (
                  projectDetails.description && (
                    <p className="text-sm text-gray-700 whitespace-pre-line">{projectDetails.description}</p>
                  )
                )}
                {Array.isArray(projectDetails.files) && projectDetails.files.length > 0 && (
                  <div className="space-y-2 mt-3">
                    <h5 className="text-xs font-semibold text-gray-600">Attached files</h5>
                    <div className="space-y-2">
                      {projectDetails.files.map((f) => (
                        <div key={f.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-3 min-w-0">
                            <IconFileText size={18} className="text-green-600 flex-shrink-0" />
                            <a className="text-sm font-medium text-gray-800 hover:underline truncate" href={f.url} target="_blank" rel="noopener noreferrer">
                              {f.filename}
                            </a>
                            {typeof f.byte_size === "number" && (
                              <span className="text-xs text-gray-500 shrink-0">({(f.byte_size / 1024 / 1024).toFixed(1)} MB)</span>
                            )}
                          </div>
                              <div className="flex items-center gap-2">
                                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900" title="Download">
                                  <IconDownload size={16} />
                                </a>
                                {isOwner && (
                                  <button
                                    className="text-red-600 hover:text-red-800"
                                    title="Delete file"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      if (!confirm('Delete this file?')) return;
                                      try {
                                        const res = await fetchApi(`/startup_entities/${startupSlug}/files/${f.id}`, 'DELETE');
                                        if (!res.ok) {
                                          const data = await res.json().catch(() => ({}));
                                          throw new Error(data.message || 'Failed to delete file');
                                        }
                                        setProjectDetails((prev) => {
                                          if (!prev) return prev;
                                          return {
                                            ...prev,
                                            files: (prev.files || []).filter((pf) => pf.id !== f.id),
                                          };
                                        });
                                      } catch (err) {
                                        console.error(err);
                                        setProjectError(err.message || 'Failed to delete file');
                                      }
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No project details available.</p>
            )}
          </div>
        )}

      {/* Confirm close/reopen */}
      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        withCloseButton
        centered
        title={desiredNextOpen ? "Reopen applications?" : "Close applications?"}
        overlayProps={{ backgroundOpacity: 0.2, blur: 2 }}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {desiredNextOpen
              ? "Applicants will be able to submit new proposals immediately."
              : "Applicants will no longer be able to submit new proposals."}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="default" onClick={() => setConfirmOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button color={desiredNextOpen ? "green" : "red"} onClick={performToggleApplications} loading={actionLoading}>
              {desiredNextOpen ? "Reopen" : "Close"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirm delete project */}
      <Modal
        opened={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        withCloseButton
        centered
        title="Delete project?"
        overlayProps={{ backgroundOpacity: 0.2, blur: 2 }}
      >
        <div className="space-y-4">
          <Alert color="red" variant="light">
            This action is permanent and will remove the project and its data. This cannot be undone.
      </Alert>
          <div className="flex justify-end gap-2">
            <Button variant="default" onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button color="red" onClick={performDeleteProject} loading={deleteLoading}>
              Delete Project
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectAlert;